import {
  ChangeEvent,
  ComponentProps,
  useEffect,
  useRef,
  useState,
  useTransition,
} from 'react';
import { useSearchParams } from 'react-router';
import { Search } from 'lucide-react';

import { DEBOUNCE_IN_MS } from '@/utils/constants';
import { Input } from '@/components/ui/input';
import InputClearButton from '@/components/input-clear-button';
import InputSpinner from '@/components/input-spinner';

export default function SearchInput(props: ComponentProps<'input'>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaultValue = searchParams.get('search') ?? '';
  const [pending, startTransition] = useTransition();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();
  const searching = Boolean(timeoutId) || pending;

  useEffect(() => {
    if (!defaultValue && inputRef?.current) {
      inputRef.current.value = '';
    }
  }, [defaultValue]);

  function onSearch(event: ChangeEvent<HTMLInputElement>) {
    clearTimeout(timeoutId);

    const { value } = event.target;
    const id = setTimeout(() => {
      startTransition(() => {
        setSearchParams((prev) => {
          prev.set('page', '1');

          if (value) {
            prev.set('search', value);
          } else {
            prev.delete('search');
          }

          return prev;
        });
      });

      setTimeoutId(undefined);
    }, DEBOUNCE_IN_MS);

    setTimeoutId(id);
  }

  function onClear() {
    startTransition(() => {
      setSearchParams((prev) => {
        prev.set('page', '1');
        prev.delete('search');

        return prev;
      });
    });

    if (inputRef?.current) {
      inputRef.current.value = '';
      inputRef.current.focus();
    }
  }

  return (
    <div className="relative">
      <Search className="text-muted-foreground absolute top-2.5 left-2 size-4" />
      <Input
        className="pl-8"
        defaultValue={defaultValue}
        name="search"
        onChange={onSearch}
        placeholder="Search"
        ref={inputRef}
        {...props}
      />
      {!searching && defaultValue && <InputClearButton onClick={onClear} />}
      {searching && <InputSpinner />}
    </div>
  );
}
